/* eslint-disable css-modules/no-undef-class */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import SpecialHotelBadge from '@/components/atoms/special-hotel-badge/special-hotel-badge';
import Typography from '@/components/atoms/typography/typography';
import GuestSatisfaction from '@/components/molecules/guest-satisfaction/guest-satisfaction';
import StarRating from '@/components/molecules/star-rating/star-rating';
import FavoriteHeart from '@/components/organisms/favorite-heart/favorite-heart';
import MediaGrid from '@/components/organisms/media-grid/media-grid';
import MediaSlider from '@/components/organisms/media-slider/media-slider';
import StarDetailsModal from '@/components/organisms/star-details-modal/star-details-modal';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useIsInView from '@/libraries/hooks/use-is-in-view';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './hotel-hero.module.scss';

export interface HotelHeroProps extends ModuleWrapperProps {
  /** The country link */
  countryLink: Content.Components['HotelPageHeroModuleDto']['countryLink'];
  /** The destination link */
  destinationLink: Content.Components['HotelPageHeroModuleDto']['destinationLink'];
  /** The destination region link */
  destinationRegionLink: Content.Components['HotelPageHeroModuleDto']['destinationLink'];
  /** The favorite id for the current offer */
  favoriteId?: Content.Components['HotelPageHeroModuleDto']['favoriteId'];
  /** Guest satisfaction */
  guestSatisfaction?: Content.Components['HotelPageHeroModuleDto']['guestSatisfaction'];
  /** The headline */
  headline: Content.Components['HotelPageHeroModuleDto']['headline'];
  /** The media items */
  items: Content.Components['HotelPageHeroModuleDto']['items'];
  /** The star rating */
  stars: Content.Components['HotelPageHeroModuleDto']['stars'];
  /** The star rating details */
  starsDetails: Content.Components['HotelPageHeroModuleDto']['starsDetails'];
}

const HotelHero = ({
  countryLink,
  destinationLink,
  destinationRegionLink,
  favoriteId,
  guestSatisfaction,
  headline,
  items = [],
  stars,
  starsDetails,
  ...rest
}: HotelHeroProps) => {
  const { t } = useI18n();
  const isTablet = useWindowSize('tablet');
  const moduleRef = useRef<HTMLDivElement>(null);
  const [isFavoriteOffer, setIsFavoriteOffer] = useState(false);
  // ! NOTE: Using -50px top to trigger the display of the headline in the in-page navigation slightly before the hero leaves view
  // ! NOTE: Added to prevent a bug when scrolling slowly down the page causing flicker
  const { isInView } = useIsInView(moduleRef, {
    rootMargin: `-${isTablet ? 50 : 20}px 0px 0px 0px`,
  });
  const { isScrolled, setIsVisibleInPageHeader } = usePageDetailsContext();
  const { specialHotelType } = useHotelContext();
  const { favoriteOffers, addFavoriteOffer, isFavorite, removeFavoriteOffer } =
    useFavoritesContext();
  const [showStarDetailsModal, setShowStarDetailsModal] = useState(false);

  useEffect(() => {
    setIsFavoriteOffer(favoriteId ? isFavorite(favoriteId) : false);
  }, [favoriteOffers, isFavorite, favoriteId]);

  useEffect(() => {
    if (!moduleRef.current) return;

    setIsVisibleInPageHeader(!isInView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView, isScrolled, moduleRef]);

  const mediaItems = useMemo<Content.Properties.ImageAndVideoGridItem[]>(() => {
    return items.map(item => {
      if ('video' in item) {
        return {
          ...item,
          $type: 'ImageAndVideoGridYoutubeItemDto',
        } as Content.Properties.ImageAndVideoGridItem;
      }

      return {
        ...item,
        $type: 'ImageAndVideoGridImageItemDto',
      } as Content.Properties.ImageAndVideoGridItem;
    });
  }, [items]);

  const renderLinks = useMemo(() => {
    const destination = destinationLink && (
      <Link target={destinationLink.target} url={destinationLink.url}>
        {destinationLink.text}
      </Link>
    );

    const destinationRegion = destinationRegionLink && (
      <Link
        className={styles.destinationRegionLink}
        target={destinationRegionLink.target}
        url={destinationRegionLink.url}
      >
        {destinationRegionLink.text}
      </Link>
    );

    const country = countryLink && (
      <Link
        className={styles.countryLink}
        target={countryLink.target}
        url={countryLink.url}
      >
        {countryLink.text}
      </Link>
    );

    const links = [destination, destinationRegion, country].filter(Boolean);

    if (!links.length) return null;

    return (
      <div className={styles.links}>
        <Icon className={styles.icon} name="Location" />

        {links.map((link, index) => (
          <Fragment key={index}>
            {index > 0 && ','}
            {link}
          </Fragment>
        ))}
      </div>
    );
  }, [countryLink, destinationLink, destinationRegionLink]);

  const handleClickFavorite = () => {
    if (!favoriteId) return;

    if (isFavoriteOffer) {
      removeFavoriteOffer(favoriteId);
    } else {
      addFavoriteOffer(favoriteId);
    }
  };

  const renderButtonWrapper = () => {
    if (!favoriteId) return null;

    return (
      <div className={styles.buttonWrapper}>
        <FavoriteHeart
          isFavorite={isFavoriteOffer}
          onClickFavorite={() => handleClickFavorite()}
          size={24}
        />
      </div>
    );
  };

  return (
    <ModuleWrapper {...rest} ref={moduleRef} padding="none">
      <Container className={styles.component}>
        <Row>
          <Column>
            {specialHotelType && (
              <SpecialHotelBadge
                className={styles.specialHotelBadge}
                type={specialHotelType}
              />
            )}

            <div className={styles.mediaGridWrapper}>
              {isTablet ? (
                <MediaGrid items={mediaItems} priority />
              ) : (
                <MediaSlider items={mediaItems} priority />
              )}

              {renderButtonWrapper()}
            </div>
          </Column>
        </Row>

        <Row>
          <Column className={styles.contentWrapper}>
            <div className={styles.nameAndLinks}>
              <Typography
                className={styles.hotelName}
                tag="h1"
                tagStyle="displayMedium"
              >
                {headline}
              </Typography>

              <div className={styles.ratingAndLinks}>
                <div className={styles.ratingWrapper}>
                  {stars && <StarRating rating={stars} />}

                  {starsDetails && (
                    <button
                      aria-label={t('items.common.words.starRatingDetails')}
                      className={styles.infoIcon}
                      onClick={() => setShowStarDetailsModal(true)}
                    >
                      <Icon name="Info" />
                    </button>
                  )}
                </div>

                {renderLinks}
              </div>
            </div>

            <div className={styles.guestSatisfactionWrapper}>
              {guestSatisfaction && (
                <GuestSatisfaction {...guestSatisfaction} />
              )}
            </div>
          </Column>
        </Row>
      </Container>

      {showStarDetailsModal && starsDetails && (
        <StarDetailsModal
          description={starsDetails.text}
          headline={starsDetails.headline}
          isOpen={showStarDetailsModal}
          onClose={() => setShowStarDetailsModal(false)}
        />
      )}
    </ModuleWrapper>
  );
};

HotelHero.displayName = 'HotelHero';

export default HotelHero;
